import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ThemeProvider } from './contexts/ThemeProvider'
import { Suspense } from 'react'
import DashboardLayout from './components/dashboard/DashboardLayout'
import ProfilePage from './pages/public/ProfilePage'
import BookingPage from './pages/public/BookingPage'
import BookingFlowPage from './pages/public/BookingFlowPage'
import Login from './pages/auth/Login'
import Register from './pages/auth/Register'
import SocialCallback from './pages/auth/SocialCallback'
import Dashboard from './pages/dashboard/Dashboard'
import ProfileEditor from './pages/dashboard/ProfileEditor'
import GalleryManager from './pages/dashboard/GalleryManager'
import SocialLinksEditor from './pages/dashboard/SocialLinksEditor'
import ServiceManager from './pages/dashboard/ServiceManager'
import AvailabilityEditor from './pages/dashboard/AvailabilityEditor'
import QRCodeSettings from './pages/dashboard/QRCodeSettings'
import BookingManager from './pages/dashboard/BookingManager'
import BookingSettings from './pages/dashboard/BookingSettings'
import CustomerList from './pages/dashboard/CustomerList'
import CustomerDetail from './pages/dashboard/CustomerDetail'
import Analytics from './pages/dashboard/Analytics'
import AppearanceSettings from './pages/dashboard/AppearanceSettings'
import VisibilitySettings from './pages/dashboard/VisibilitySettings'
import Subscription from './pages/dashboard/Subscription'
import BookingAddons from './pages/dashboard/BookingAddons'
import PeakPricingEditor from './pages/dashboard/PeakPricingEditor'
import Referrals from './pages/dashboard/Referrals'
import MaterialsManager from './pages/dashboard/MaterialsManager'
import LocationsPage from './pages/dashboard/LocationsPage'
import SchedulePage from './pages/dashboard/SchedulePage'
import BreakTimesEditor from './pages/dashboard/BreakTimesEditor'
import TeamManager from './pages/dashboard/TeamManager'
import Landing from './pages/Landing'
import CancelBooking from './pages/guest/CancelBooking'
import TeamInviteAccept from './pages/teams/TeamInviteAccept'
import AuthCallback from './pages/auth/AuthCallback'
import OfferingsHub from './pages/dashboard/hubs/OfferingsHub'
import HoursHub from './pages/dashboard/hubs/HoursHub'
import ProfileHub from './pages/dashboard/hubs/ProfileHub'
import BrandingHub from './pages/dashboard/hubs/BrandingHub'
import BillingHub from './pages/dashboard/hubs/BillingHub'
import { ProfileLocationProvider } from './contexts/ProfileLocationContext'
import { useAuth } from './hooks/useAuth'
import { useSubscription } from './hooks/useSubscription'

const queryClient = new QueryClient()

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()
  
  if (isLoading) {
    return <div>Loading...</div>
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }
  
  return <>{children}</>
}

/**
 * Route wrapper that checks for a required feature flag.
 * Redirects to dashboard if user doesn't have the feature.
 */
function FeatureRoute({ children, requiredFeature }: { children: React.ReactNode; requiredFeature: string }) {
  const { hasFeature, isLoading } = useSubscription()
  
  if (isLoading) {
    return <div>Loading...</div>
  }
  
  if (!hasFeature(requiredFeature)) {
    return <Navigate to="/dashboard" replace />
  }
  
  return <>{children}</>
}

function App() {
  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider defaultTheme="system" storageKey="de-zign-theme">
          <Suspense fallback={<div>Loading...</div>}>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<Landing />} />
              <Route path="/:username" element={<ProfilePage />} />
              <Route path="/book/:username" element={<BookingPage />} />
              <Route path="/book/staff/:userId" element={<BookingFlowPage entryMode="staff" variant="page" />} />
              <Route path="/embed/book/:username" element={<BookingFlowPage entryMode="location" variant="embed" />} />
              <Route path="/embed/book/staff/:userId" element={<BookingFlowPage entryMode="staff" variant="embed" />} />
              <Route path="/guest/cancel/:token" element={<CancelBooking />} />
              <Route path="/teams/invite/accept" element={<TeamInviteAccept />} />
              
              {/* Auth routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/auth/social/callback/:provider" element={<SocialCallback />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              
              {/* Dashboard routes */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <ProfileLocationProvider>
                      <DashboardLayout />
                    </ProfileLocationProvider>
                  </ProtectedRoute>
                }
              >
                <Route index element={<Dashboard />} />
                <Route path="schedule" element={<SchedulePage />} />
                <Route path="bookings" element={<BookingManager />} />
                <Route path="booking-settings" element={<BookingSettings />} />
                <Route path="customers" element={<CustomerList />} />
                <Route path="customers/:id" element={<CustomerDetail />} />
                <Route path="analytics" element={
                  <FeatureRoute requiredFeature="profile.analytics">
                    <Analytics />
                  </FeatureRoute>
                } />
                <Route path="team" element={<TeamManager />} />

                {/* Hub: Offerings */}
                <Route path="offerings" element={<OfferingsHub />}>
                  <Route index element={<Navigate to="services" replace />} />
                  <Route path="services" element={<ServiceManager />} />
                  <Route path="materials" element={<MaterialsManager />} />
                </Route>

                {/* Hub: Hours & locations */}
                <Route path="hours" element={<HoursHub />}>
                  <Route index element={<Navigate to="availability" replace />} />
                  <Route path="availability" element={<AvailabilityEditor />} />
                  <Route path="breaks" element={<BreakTimesEditor />} />
                  <Route path="peak-pricing" element={
                    <FeatureRoute requiredFeature="pricing.dynamic">
                      <PeakPricingEditor />
                    </FeatureRoute>
                  } />
                  <Route path="locations" element={<LocationsPage />} />
                </Route>

                {/* Hub: Profile */}
                <Route path="profile" element={<ProfileHub />}>
                  <Route index element={<Navigate to="details" replace />} />
                  <Route path="details" element={<ProfileEditor />} />
                  <Route path="gallery" element={<GalleryManager />} />
                  <Route path="social" element={<SocialLinksEditor />} />
                </Route>

                {/* Hub: Branding */}
                <Route path="branding" element={<BrandingHub />}>
                  <Route index element={<Navigate to="appearance" replace />} />
                  <Route path="appearance" element={<AppearanceSettings />} />
                  <Route path="visibility" element={<VisibilitySettings />} />
                  <Route path="qrcode" element={<QRCodeSettings />} />
                </Route>

                {/* Hub: Billing */}
                <Route path="billing" element={<BillingHub />}>
                  <Route index element={<Navigate to="subscription" replace />} />
                  <Route path="subscription" element={<Subscription />} />
                  <Route path="booking-packs" element={<BookingAddons />} />
                  <Route path="referrals" element={<Referrals />} />
                </Route>

                {/* Legacy redirects */}
                <Route path="services" element={<Navigate to="/dashboard/offerings/services" replace />} />
                <Route path="materials" element={<Navigate to="/dashboard/offerings/materials" replace />} />
                <Route path="booking-addons" element={<Navigate to="/dashboard/billing/booking-packs" replace />} />
                <Route path="availability" element={<Navigate to="/dashboard/hours/availability" replace />} />
                <Route path="break-times" element={<Navigate to="/dashboard/hours/breaks" replace />} />
                <Route path="peak-pricing" element={<Navigate to="/dashboard/hours/peak-pricing" replace />} />
                <Route path="locations" element={<Navigate to="/dashboard/hours/locations" replace />} />
                <Route path="gallery" element={<Navigate to="/dashboard/profile/gallery" replace />} />
                <Route path="appearance" element={<Navigate to="/dashboard/branding/appearance" replace />} />
                <Route path="visibility" element={<Navigate to="/dashboard/branding/visibility" replace />} />
                <Route path="qrcode" element={<Navigate to="/dashboard/branding/qrcode" replace />} />
                <Route path="subscription" element={<Navigate to="/dashboard/billing/subscription" replace />} />
                <Route path="referrals" element={<Navigate to="/dashboard/billing/referrals" replace />} />
              </Route>
            </Routes>
          </Suspense>
        </ThemeProvider>
      </QueryClientProvider>
    </BrowserRouter>
  )
}

export default App
